/**
 * BehaviorTreeLoader - loads behavior trees from JSON definitions.
 */

import * as THREE from 'three';

// Import all actions from organized subfolders
import { resourceManager } from '../../../utils/ResourceManager.js';
import {
  AerialMoveToTargetAction,
  AerialSearchAction,
  AerialTrackTargetAction,
  AerialWanderAction
} from '../behaviors/actions/aerial/index.js';
import {
  MeleeAttackAction,
  ShootAction
} from '../behaviors/actions/combat/index.js';
import {
  FleeFromTargetAction,
  FollowActorAction,
  MoveToPositionAction,
  SetNextWaypointAction,
  WanderAction
} from '../behaviors/actions/ground/index.js';
import {
  LookAtTargetAction,
  WaitAction
} from '../behaviors/actions/utility/index.js';
import { CanSeeTargetCondition } from '../behaviors/conditions/CanSeeTargetCondition.js';
import { HasLastKnownPositionCondition } from '../behaviors/conditions/HasLastKnownPositionCondition.js';
import { HpAbovePercentCondition } from '../behaviors/conditions/HpAbovePercentCondition.js';
import { IsPlayerNearCondition } from '../behaviors/conditions/IsPlayerNearCondition.js';
import { TargetBelowHpPercentCondition } from '../behaviors/conditions/TargetBelowHpPercentCondition.js';
import { TargetInConeCondition } from '../behaviors/conditions/TargetInConeCondition.js';
import { TimeLimitCondition } from '../behaviors/conditions/TimeLimitCondition.js';
import { SelectorNode } from '../nodes/composite/SelectorNode.js';
import { SequenceNode } from '../nodes/composite/SequenceNode.js';

import type { AssetPath } from '../../../storage/storageProvider.js';
import type { BehaviorNode } from '../BehaviorNode.js';
import type { ConditionEvaluator } from '../behaviors/ConditionEvaluator.js';

interface BehaviorTreeJSON {
  name: string;
  description?: string;
  blackboard?: { [key: string]: any };
  rootNode: BehaviorNodeJSON;
}

interface BehaviorNodeJSON {
  type: string;
  name?: string;
  description?: string;
  children?: BehaviorNodeJSON[];
  child?: BehaviorNodeJSON; // For decorator nodes
  condition?: BehaviorNodeJSON; // For conditional decorators
  conditions?: BehaviorNodeJSON[]; // For attached conditions (Unreal Engine style)
  [key: string]: any; // For node-specific properties
}

export class BehaviorTreeLoader {
  private static nodeRegistry: Map<string, new (options: any) => BehaviorNode> = new Map();
  private static conditionRegistry: Map<string, new (options: any) => ConditionEvaluator> = new Map();

  static {
    // Initialize the node registry
    this.nodeRegistry.set('Sequence', SequenceNode as any);
    this.nodeRegistry.set('Selector', SelectorNode as any);
    this.nodeRegistry.set('AerialMoveToTarget', AerialMoveToTargetAction as any);
    this.nodeRegistry.set('AerialSearch', AerialSearchAction as any);
    this.nodeRegistry.set('AerialTrackTarget', AerialTrackTargetAction as any);
    this.nodeRegistry.set('AerialWander', AerialWanderAction as any);
    this.nodeRegistry.set('MoveToPosition', MoveToPositionAction as any);
    this.nodeRegistry.set('SetNextWaypoint', SetNextWaypointAction as any);
    this.nodeRegistry.set('FollowActor', FollowActorAction as any);
    this.nodeRegistry.set('FleeFromTarget', FleeFromTargetAction as any);
    this.nodeRegistry.set('LookAtTarget', LookAtTargetAction as any);
    this.nodeRegistry.set('Wait', WaitAction as any);
    this.nodeRegistry.set('MeleeAttack', MeleeAttackAction as any);
    this.nodeRegistry.set('Shoot', ShootAction as any);
    this.nodeRegistry.set('Wander', WanderAction as any);

    // Initialize the condition registry
    this.conditionRegistry.set('IsPlayerNear', IsPlayerNearCondition as any);
    this.conditionRegistry.set('CanSeeTarget', CanSeeTargetCondition as any);
    this.conditionRegistry.set('HasLastKnownPosition', HasLastKnownPositionCondition as any);
    this.conditionRegistry.set('HpAbovePercent', HpAbovePercentCondition as any);
    this.conditionRegistry.set('TimeLimit', TimeLimitCondition as any);
    this.conditionRegistry.set('TargetInCone', TargetInConeCondition as any);
    this.conditionRegistry.set('TargetBelowHpPercent', TargetBelowHpPercentCondition as any);
  }

  public static registerNodeType(typeName: string, nodeClass: new (options: any) => BehaviorNode): void {
    this.nodeRegistry.set(typeName, nodeClass);
  }

  public static registerConditionType(typeName: string, conditionClass: new (options: any) => ConditionEvaluator): void {
    this.conditionRegistry.set(typeName, conditionClass);
  }

  public static async loadFromFile(path: AssetPath): Promise<{ rootNode: BehaviorNode; blackboardData?: { [key: string]: any } }> {
    try {
      const json: BehaviorTreeJSON = await resourceManager.loadJsonObject(path);
      return this.loadFromJSON(json);
    } catch (error) {
      console.error('BehaviorTreeLoader: Failed to load from file:', path, error);
      throw error;
    }
  }

  public static loadFromJSON(json: BehaviorTreeJSON): { rootNode: BehaviorNode; blackboardData?: { [key: string]: any } } {
    const rootNode = this.createNodeFromJSON(json.rootNode);

    // Extract blackboard initial values if specified
    let blackboardData: { [key: string]: any } | undefined;
    if (json.blackboard) {
      blackboardData = {};
      for (const [key, config] of Object.entries(json.blackboard)) {
        if (config.defaultValue !== undefined) {
          // Handle Vector3 arrays specially
          if (config.type === 'Vector3[]' && Array.isArray(config.defaultValue)) {
            const vectors = config.defaultValue.map((v: any) => new THREE.Vector3(v.x, v.y, v.z));
            blackboardData[key] = vectors;
          } else {
            blackboardData[key] = config.defaultValue;
          }
        }
      }
    }

    return { rootNode, blackboardData };
  }

  private static createNodeFromJSON(nodeJson: BehaviorNodeJSON): BehaviorNode {
    const NodeClass = this.nodeRegistry.get(nodeJson.type);
    if (!NodeClass) {
      throw new Error(`Unknown node type: ${nodeJson.type}`);
    }

    // Extract node-specific options
    const options: any = {
      name: nodeJson.name,
      description: nodeJson.description,
    };

    // Handle attached conditions (Unreal Engine style)
    if (nodeJson.conditions) {
      options.conditions = nodeJson.conditions.map(conditionJson => this.createConditionFromJSON(conditionJson));
    }

    // Copy all properties except type, name, description, children, child, condition, and conditions
    for (const [key, value] of Object.entries(nodeJson)) {
      if (!['type', 'name', 'description', 'children', 'child', 'condition', 'conditions'].includes(key)) {
        // Pass all property values directly to the node constructor
        options[key] = value;
      }
    }

    // Handle children for composite nodes
    if (nodeJson.children) {
      options.children = nodeJson.children.map(childJson => this.createNodeFromJSON(childJson));
    }

    // Handle child for decorator nodes
    if (nodeJson.child) {
      options.child = this.createNodeFromJSON(nodeJson.child);
    }

    // Handle condition for conditional decorator nodes
    if (nodeJson.condition) {
      options.condition = this.createNodeFromJSON(nodeJson.condition);
    }

    return new NodeClass(options);
  }

  private static createConditionFromJSON(conditionJson: BehaviorNodeJSON): ConditionEvaluator {
    const ConditionClass = this.conditionRegistry.get(conditionJson.type);
    if (!ConditionClass) {
      throw new Error(`Unknown condition type: ${conditionJson.type}`);
    }

    // Extract condition-specific options
    const options: any = {
      name: conditionJson.name,
      description: conditionJson.description,
    };

    // Copy all properties except type, name, and description
    for (const [key, value] of Object.entries(conditionJson)) {
      if (!['type', 'name', 'description'].includes(key)) {
        // Pass all property values directly to the condition constructor
        options[key] = value;
      }
    }

    return new ConditionClass(options);
  }

  public static getRegisteredTypes(): string[] {
    return Array.from(this.nodeRegistry.keys());
  }

  public static getRegisteredConditionTypes(): string[] {
    return Array.from(this.conditionRegistry.keys());
  }

  public static isNodeTypeRegistered(typeName: string): boolean {
    return this.nodeRegistry.has(typeName);
  }

  public static isConditionTypeRegistered(typeName: string): boolean {
    return this.conditionRegistry.has(typeName);
  }

  public static registerNodeTypes(types: Record<string, new (options: any) => BehaviorNode>): void {
    for (const [typeName, nodeClass] of Object.entries(types)) {
      this.registerNodeType(typeName, nodeClass);
    }
  }

  public static registerConditionTypes(types: Record<string, new (options: any) => ConditionEvaluator>): void {
    for (const [typeName, conditionClass] of Object.entries(types)) {
      this.registerConditionType(typeName, conditionClass);
    }
  }

  public static clearCustomRegistrations(): void {
    // Keep only the built-in types
    const builtInNodes = new Map([
      ['Sequence', SequenceNode as any],
      ['Selector', SelectorNode as any],
      ['MoveToPosition', MoveToPositionAction as any],
      ['SetNextWaypoint', SetNextWaypointAction as any],
      ['FollowActor', FollowActorAction as any],
      ['FleeFromTarget', FleeFromTargetAction as any],
      ['LookAtTarget', LookAtTargetAction as any],
      ['Wait', WaitAction as any],
      ['MeleeAttack', MeleeAttackAction as any],
      ['Shoot', ShootAction as any],
      ['Wander', WanderAction as any],
    ]);

    const builtInConditions = new Map([
      ['IsPlayerNear', IsPlayerNearCondition as any],
      ['CanSeeTarget', CanSeeTargetCondition as any],
      ['HpAbovePercent', HpAbovePercentCondition as any],
      ['TimeLimit', TimeLimitCondition as any],
      ['TargetInCone', TargetInConeCondition as any],
      ['TargetBelowHpPercent', TargetBelowHpPercentCondition as any],
    ]);

    this.nodeRegistry = builtInNodes;
    this.conditionRegistry = builtInConditions;
  }
}